import React from 'react';
import { Star } from 'lucide-react';

/**
 * StarRating Component - Displays star rating with support for half stars
 * 
 * 10 total stars with filled proportionate to rating:
 * - 8.5 rating = 8 filled + 1 half + 1 empty
 * - 7.0 rating = 7 filled + 3 empty
 * - 9.5 rating = 9 filled + 1 half + 0 empty
 * 
 * @param {number} value - Rating value (0-10, supports decimals like 8.5)
 * @param {function} onChange - Optional callback when rating changes (makes it interactive)
 * @param {string} size - Size variant: 'xs', 'sm', 'md', 'lg'
 * @param {boolean} disabled - Whether interaction is disabled
 * @param {boolean} showValue - Whether to show the numeric value
 * @param {boolean} showLabel - Whether to show the rating label (Excellent, Good, etc.)
 */
export const StarRating = ({ 
  value = 5, 
  onChange = null, 
  size = 'md', 
  disabled = false,
  showValue = false,
  showLabel = false
}) => {
  const total = 10;
  const goldColor = '#C6A34A';
  
  // Size configurations
  const sizeConfig = {
    xs: { star: 'w-3 h-3', gap: 'gap-0.5', text: 'text-xs', label: 'text-xs' },
    sm: { star: 'w-4 h-4', gap: 'gap-0.5', text: 'text-sm', label: 'text-xs' },
    md: { star: 'w-5 h-5', gap: 'gap-1', text: 'text-base', label: 'text-sm' },
    lg: { star: 'w-6 h-6', gap: 'gap-1', text: 'text-lg', label: 'text-base' }
  };
  
  const config = sizeConfig[size] || sizeConfig.md;
  const isInteractive = onChange !== null && !disabled;
  
  // Get rating label based on ranges:
  // 8-10: EXCELLENT, 5.5-7.5: GOOD, 3.5-5: AVERAGE, 1-3: POOR
  const getRatingLabel = (rating) => {
    if (rating >= 8) return { text: 'Excellent', color: 'text-slate-800' };
    if (rating >= 5.5) return { text: 'Good', color: 'text-slate-800' };
    if (rating >= 3.5) return { text: 'Average', color: 'text-slate-800' };
    return { text: 'Poor', color: 'text-slate-800' };
  };
  
  const label = getRatingLabel(value);
  
  return (
    <div className="flex items-center flex-wrap gap-2">
      <div className={`flex ${config.gap} items-center`}>
        {Array.from({ length: total }, (_, i) => i + 1).map((star) => {
          // Determine star fill state
          const isFull = value >= star;
          const isHalf = value >= star - 0.5 && value < star;
          
          return (
            <div 
              key={star} 
              className={`relative ${config.star} ${isInteractive ? 'cursor-pointer' : ''}`}
            >
              {/* Empty star with gold outline (always visible as base) */}
              <Star 
                className="w-full h-full absolute" 
                style={{ 
                  color: goldColor,
                  fill: 'transparent',
                  strokeWidth: 2
                }} 
              />
              
              {/* Half star fill - clip left half */}
              {isHalf && (
                <div className="absolute inset-0 overflow-hidden" style={{ width: '50%' }}>
                  <Star 
                    className="w-full h-full" 
                    style={{ 
                      color: goldColor,
                      fill: goldColor,
                      strokeWidth: 2
                    }} 
                  />
                </div>
              )}
              
              {/* Full star fill */}
              {isFull && (
                <Star 
                  className="w-full h-full absolute" 
                  style={{ 
                    color: goldColor,
                    fill: goldColor,
                    strokeWidth: 2
                  }} 
                />
              )}
              
              {/* Interactive click areas - left half for .5, right half for full */}
              {isInteractive && (
                <div className="absolute inset-0 flex">
                  <button
                    type="button"
                    className="w-1/2 h-full"
                    onClick={() => onChange(star - 0.5)}
                    title={`Rate ${star - 0.5}`}
                  />
                  <button
                    type="button"
                    className="w-1/2 h-full"
                    onClick={() => onChange(star)}
                    title={`Rate ${star}`}
                  />
                </div>
              )}
            </div>
          );
        })}
      </div>
      
      {showValue && (
        <span className={`font-semibold text-slate-700 ${config.text}`}>
          {value}/10
        </span>
      )}
      
      {showLabel && (
        <span className={`font-medium ${label.color} ${config.label}`}>
          {label.text}
        </span>
      )}
    </div>
  );
};

/**
 * Simple read-only star display for compact views (like dashboard cards)
 * Shows 10 stars with proper half-star support
 */
export const StarDisplayCompact = ({ rating = 5, size = 'sm' }) => {
  const total = 10;
  const goldColor = '#C6A34A';
  const emptyColor = '#CBD5E1'; // slate-300
  
  const sizeConfig = {
    xs: 'w-2.5 h-2.5',
    sm: 'w-3.5 h-3.5',
    md: 'w-4 h-4'
  };
  
  const gapConfig = {
    xs: 'gap-0.5',
    sm: 'gap-0.5',
    md: 'gap-1'
  };
  
  const starSize = sizeConfig[size] || sizeConfig.sm;
  const gapSize = gapConfig[size] || gapConfig.sm;
  
  return (
    <div className={`flex ${gapSize}`}>
      {Array.from({ length: total }, (_, i) => i + 1).map((star) => {
        const isFull = rating >= star;
        const isHalf = rating >= star - 0.5 && rating < star;
        
        return (
          <div key={star} className={`relative ${starSize}`}>
            {/* Base empty star */}
            <Star 
              className="w-full h-full absolute"
              style={{ 
                color: emptyColor,
                fill: emptyColor,
                strokeWidth: 0
              }} 
            />
            
            {/* Half star overlay */}
            {isHalf && (
              <div className="absolute inset-0 overflow-hidden" style={{ width: '50%' }}>
                <Star 
                  className="w-full h-full"
                  style={{ 
                    color: goldColor,
                    fill: goldColor,
                    strokeWidth: 0
                  }} 
                />
              </div>
            )}
            
            {/* Full star overlay */}
            {isFull && (
              <Star 
                className="w-full h-full absolute"
                style={{ 
                  color: goldColor,
                  fill: goldColor,
                  strokeWidth: 0
                }} 
              />
            )}
          </div>
        );
      })}
    </div>
  );
};

export default StarRating;
